import IconWithFallback from "Global/IconWithFallback";
import { EXCHANGES, ICON_TYPES } from "@shared/common/constants";
import React from "react";
import Flex from "common/Boxes/Flex";
import Title from "common/Typography/Title";
import Tag from "common/Tags/Tag";
import styled, { useTheme } from "styled-components";
import Tooltip from "common/Tooltips/Tooltip";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import { useBottomNavigator } from "utils/hooks/misc";
import { MOBILE_SEARCH } from "common/constants";
import useMedia from "utils/hooks/useMedia.ts";
import { CREATE_ACCOUNT_EXCHANGE_CARD_ID } from "testIds.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { phoneMinWidth } from "common/NewLook/layout.ts";

interface ExchangeCardProps {
  primaryExchange: string;
  tags?: string[];
  onSelectExchange?: (primaryExchange: string) => void;
  isPremiumIncluded?: boolean;
  isSameRowAsPremium?: boolean;
}

const ExchangeCard = ({
  primaryExchange,
  tags,
  onSelectExchange,
  isPremiumIncluded,
  isSameRowAsPremium,
}: ExchangeCardProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const theme = useTheme();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isSmallMobile = useMedia([`(max-width: 400px)`], [true], false);
  const iconSize = isBottomNavigator ? MOBILE_ICON_SIZE : ICON_SIZE;

  return (
    <Wrapper
      onClick={() => onSelectExchange?.(primaryExchange)}
      $isPremiumIncluded={isPremiumIncluded}
      $isSameRowAsPremium={isSameRowAsPremium}
      data-testid={`${CREATE_ACCOUNT_EXCHANGE_CARD_ID}-${primaryExchange.toLowerCase()}`}
    >
      <Card
        $hideHover={isBottomNavigator}
        $isPremiumIncluded={isPremiumIncluded}
        $isSameRowAsPremium={isSameRowAsPremium}
      >
        <Flex
          $justifyContent="flex-start"
          $alignItems={isMobile ? "flex-start" : "center"}
          $flexDirection={isMobile ? "column" : "row"}
        >
          <Flex $justifyContent="flex-start">
            <IconWithFallback
              iconType={ICON_TYPES.EXCHANGE}
              coinOrExchangeName={primaryExchange}
              iconStyle={{
                width: iconSize,
                height: iconSize,
                marginRight: isMobile ? 12 : 16,
                filter:
                  primaryExchange === EXCHANGES.OKX && theme.isDarkMode
                    ? `invert(1)`
                    : undefined,
              }}
            />
            {isMobile && <Exchange>{primaryExchange}</Exchange>}
          </Flex>
          <div>
            {!isMobile && <Exchange>{primaryExchange}</Exchange>}
            <TagsWrapper>
              {tags?.map((type, index) => (
                <Tag
                  key={index}
                  color={theme.colors.secondaryGrey}
                  bgColor={
                    theme.isDarkMode
                      ? theme.colors.tagBgColor
                      : theme.colors.statisticsBgColor
                  }
                  style={{
                    whiteSpace: "nowrap",
                    fontSize: isSmallMobile ? 10 : 11,
                  }}
                >
                  {type}
                </Tag>
              ))}
            </TagsWrapper>
          </div>
        </Flex>
      </Card>
      {isPremiumIncluded && (
        <Tooltip
          title={t("create_account.premium_plan_included_tooltip")}
          showMobileBottomDrawer={true}
          mobileTitle={t("create_account.premium_plan_included")}
          placement="bottom"
        >
          <PremiumIncludedTag
            onClick={(e) => e.stopPropagation()}
            aria-label={t("create_account.premium_plan_included_tooltip")}
          >
            <div>{t("create_account.premium_plan_included")}</div>
            <PremiumIncludedIcon />
          </PremiumIncludedTag>
        </Tooltip>
      )}
    </Wrapper>
  );
};

export default ExchangeCard;

const MOBILE_ICON_SIZE = 28;
const ICON_SIZE = 32;

const Wrapper = styled.div<{
  $isPremiumIncluded?: boolean;
  $isSameRowAsPremium?: boolean;
}>`
  position: relative;
  height: 100%;
  ${(p) => p.$isPremiumIncluded && `padding-bottom: 12px;`}
`;
const Card = styled.div<{
  $hideHover?: boolean;
  $isPremiumIncluded?: boolean;
  $isSameRowAsPremium?: boolean;
}>`
  padding: 12px 16px;
  border: ${(p) => p.theme.borders.light};
  border-radius: 12px;
  cursor: pointer;
  height: fit-content;
  ${(p) =>
    p.$isPremiumIncluded &&
    `
    border: 2px solid ${p.theme.colors.primaryBlue} !important;
    padding-bottom: 16px;
  `}
  ${(p) =>
    p.$isSameRowAsPremium && !p.$isPremiumIncluded && `padding-bottom: 18px;`}
  &:hover {
    @media (min-width: ${phoneMinWidth}) {
      border-color: ${(p) => p.theme.colors.primaryBlue};
    }
  }
  transition: border-color 0.4s ease-in-out;
  @media (max-width: ${MOBILE_SEARCH}) {
    padding: 10px;
  }
`;
const PremiumIncludedTag = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${(p) => p.theme.colors.primaryBlue};
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translate(-50%, 50%);
  color: ${(p) => p.theme.colors.buttonText};
  border-radius: 20px;
  padding: 4px 12px;
  z-index: 2;
  white-space: nowrap;
  font-size: ${(p) => p.theme.typography.fontSizes.XS};
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  column-gap: 6px;
  cursor: help;
  transition: background-color 0.2s ease-in-out, filter 0.2s ease-in-out;

  &:hover {
    filter: brightness(1.08);
  }
`;
const PremiumIncludedIcon = styled(InfoCircleOutlined)`
  font-size: 12px;
  margin-bottom: 1px;
`;
const Exchange = styled(Title).attrs({ level: 4 })`
  @media (min-width: ${MOBILE_SEARCH}) {
    margin-bottom: 4px;
  }
`;
const TagsWrapper = styled(Flex)`
  column-gap: 4px;
  justify-content: flex-start;
  @media (max-width: ${MOBILE_SEARCH}) {
    margin-top: 8px;
  }
  @media (max-width: 355px) {
    flex-wrap: wrap;
    row-gap: 4px;
  }
`;
